import React, { useState } from 'react';
import Banner from '@cpg-its/ads.containers.banner';
import { Link } from 'react-router-dom';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import WarningIcon from '@material-ui/icons/Warning';
import InfoIcon from '@material-ui/icons/Info';
import { Accordion, AccordionDetails, AccordionSummary } from '@material-ui/core';
import { InstitutionProps } from '../../../models/arc-epi-props';
import { PlanSummary } from '../institution-model';
import { CurrentPlan, getCurrentPlan, PlanStatus } from './plan-summary-helper';
import { LabelAndValue } from '../../../../../global/components/data-display/label-value';
import { getFormattedDate } from '../../../../../global/helpers/date-helpers';
import { buttonStyles } from '../../../../../global/constants/theme/styles/components/buttonstyles';
import { ContentSpacer } from '../../../../../global/components/layout/generic-content-spacer';
import { WysiwygWithReactLink } from '../../../../../global/components/wysiwyg-with-react-link';
import { useQuery } from '../../../../../global/hooks/useQuery';
import { useBasePath } from '../../../../../global/hooks/useBasePath';
import { ArcRoutes } from '../../../constants/routes';
import { PensionPlanTypes } from '../plan-history/plan-history-models';
import { labelValueStyles } from '../../../../../global/constants/theme/styles/components/label-value-styles';
import { headerIconStyles } from '../../../../../global/constants/theme/styles/components/header-icon-styles';
import { AccordionHeader } from '../../../../../Global/components/layout/accordion-header';


interface Props {
    settings: InstitutionProps,
    planSummary: PlanSummary,
    downloadPdfAction: any,
}

function PensionPlanSummary(props: Props) {

    const { settings, planSummary, downloadPdfAction } = props;
    const layDc = getCurrentPlan(planSummary?.dc);
    const layDb = getCurrentPlan(planSummary?.lay_db);
    const clergyDb = getCurrentPlan(planSummary?.clergy_db);
    const rsvp = getCurrentPlan(planSummary?.rsvp);
    const [expandAll, setExpandAll] = useState(false);
    const [expand1, setExpand1] = useState(false);
    const [expand2, setExpand2] = useState(false);
    const [expand3, setExpand3] = useState(false);
    const [expand4, setExpand4] = useState(false);
    const query = useQuery().get('test');
    const buttonClasses = buttonStyles();
    const planHistoryLink = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.InstitutionRoot}${ArcRoutes.PlanHistory}`);
    const qccoPlanAmendLink = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.InstitutionRoot}${ArcRoutes.QccoPlanAmend}`);
    const tableClasses = labelValueStyles();
    const headerClasses = headerIconStyles();


    const setExpandAction = () => {
        setExpandAll(!expandAll);
        setExpand1(!expandAll);
        setExpand2(!expandAll);
        setExpand3(!expandAll);
        setExpand4(!expandAll);
    };

    function RenderStatus(plan: CurrentPlan) {
        if (!plan) {
            return null;
        }

        if (query === PlanStatus.ActiveInFuture.toString()) {
            return (
                <ContentSpacer>
                    <Banner
                        banner={{
                            type: 'info',
                            label: <WysiwygWithReactLink content={settings?.planNotActiveWarningMessage} />,
                        }}
                    />
                </ContentSpacer>
            );
        }

        if (query === PlanStatus.TerminatedInFuture.toString()) {
            return (
                <ContentSpacer>
                    <Banner
                        banner={{
                            type: 'warning',
                            label: <WysiwygWithReactLink content={settings?.planTerminationWarningMessage} />,
                        }}
                    />
                </ContentSpacer>
            );
        }

        switch (plan?.status) {
            case PlanStatus.ActiveInFuture:
                return (
                    <ContentSpacer>
                        <Banner
                            banner={{
                                type: 'info',
                                label: <WysiwygWithReactLink content={settings?.planNotActiveWarningMessage} />,
                            }}
                        />
                    </ContentSpacer>
                );
            case PlanStatus.TerminatedInFuture:
                return (
                    <ContentSpacer>
                        <Banner
                            banner={{
                                type: 'warning',
                                label: <WysiwygWithReactLink content={settings?.planTerminationWarningMessage} />,
                            }}
                        />
                    </ContentSpacer>
                );

            default:
                return null;
        }

    }

    function RenderIconStatus(plan: CurrentPlan) {
        if (!plan) {
            return null;
        }

        if (query === PlanStatus.ActiveInFuture.toString()) {
            return <InfoIcon className='info-icon' />;
        }

        if (query === PlanStatus.TerminatedInFuture.toString()) {
            return <WarningIcon className='warning-icon' />;
        }

        switch (plan?.status) {
            case PlanStatus.ActiveInFuture:
                return <InfoIcon className='info-icon' />;
            case PlanStatus.TerminatedInFuture:
                return <WarningIcon className='warning-icon' />;

            default:
                return null;
        }

    }

    if (!layDc && !layDb && !clergyDb && !rsvp) {
        return null;
    }

    return (
        <>
            <AccordionHeader>
                <h4>{settings?.pensionPlansTitle}</h4>
                <button onClick={setExpandAction} type='button' className={buttonClasses.linkButton}>{expandAll ? settings?.closeAllText : settings?.expandAllText}</button>
            </AccordionHeader>

            {
                clergyDb &&
                <Accordion expanded={expand1} onChange={() => setExpand1(!expand1)} >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <div className={headerClasses.iconHeader}>
                            <p>{clergyDb?.plan?.name}</p>
                            {RenderIconStatus(clergyDb)}
                        </div>
                    </AccordionSummary>
                    <AccordionDetails >
                        <div>
                            <ContentSpacer>
                                {RenderStatus(clergyDb)}
                                <Link to={`${planHistoryLink}?type=${PensionPlanTypes.ClergyDb}`} >{settings?.planHistoryLinkText}</Link>
                            </ContentSpacer>
                            <div className={tableClasses.labelRowContainer}>
                                {clergyDb?.plan?.name && <LabelAndValue value={clergyDb?.plan?.name} label={settings.planNameLabel} />}
                                {clergyDb?.plan?.effective_date && <LabelAndValue value={getFormattedDate(clergyDb?.plan?.effective_date)} label={settings.adoptionDateLabel} />}
                                {clergyDb?.plan?.name && <LabelAndValue value={clergyDb?.plan?.billing_schedule} label={settings.billingSchdeuleLabel} />}
                                {clergyDb?.plan?.name && <LabelAndValue value={clergyDb?.plan?.status} label={settings.planStatusLabel} />}
                            </div>
                        </div>
                    </AccordionDetails>
                </Accordion>
            }
            {
                layDb &&
                <Accordion expanded={expand2} onChange={() => setExpand2(!expand2)}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <div className={headerClasses.iconHeader}>
                            <p>{layDb?.plan?.name}</p>
                            {RenderIconStatus(layDb)}
                        </div>
                    </AccordionSummary>
                    <AccordionDetails >
                        <div>
                            <ContentSpacer>
                                {RenderStatus(layDb)}
                                <Link to={`${planHistoryLink}?type=${PensionPlanTypes.LayDb}`} >{settings?.planHistoryLinkText}</Link>
                                    {layDb?.plan?.docusign_envelope_id && <button className={buttonClasses.linkButton} type='button' onClick={() => downloadPdfAction(layDb?.plan?.docusign_envelope_id)} > | {settings?.downloadDocusignPdfLinkText}</button>}

                            </ContentSpacer>
                            <div className={tableClasses.labelRowContainer}>
                                {layDb?.plan?.name && <LabelAndValue value={layDb?.plan?.name} label={settings.planNameLabel} />}
                                {layDb?.plan?.effective_date && <LabelAndValue value={getFormattedDate(layDb?.plan?.effective_date)} label={settings.adoptionDateLabel} />}
                                {layDb?.plan?.name && <LabelAndValue value={layDb?.plan?.billing_schedule} label={settings.billingSchdeuleLabel} />}
                                {layDb?.plan?.name && <LabelAndValue value={layDb?.plan?.status} label={settings.planStatusLabel} />}
                            </div>
                        </div>
                    </AccordionDetails>
                </Accordion>
            }
            {
                layDc &&
                <Accordion expanded={expand3} onChange={() => setExpand3(!expand3)}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <div className={headerClasses.iconHeader}>
                            <p>{layDc?.plan?.name}</p>
                            {RenderIconStatus(layDc)}
                        </div>
                    </AccordionSummary>
                    <AccordionDetails >
                        <div>
                            <ContentSpacer>
                                {RenderStatus(layDc)}
                                <Link to={`${planHistoryLink}?type=${PensionPlanTypes.LayDc}`} >{settings?.planHistoryLinkText}</Link> | <Link to={`${qccoPlanAmendLink}?formType=${PensionPlanTypes.LayDc}`} >{settings?.amendPlanLinkText}</Link>
                                {layDc?.plan?.docusign_envelope_id && <button className={buttonClasses.linkButton} type='button' onClick={() => downloadPdfAction(layDc?.plan?.docusign_envelope_id)} > | {settings?.downloadDocusignPdfLinkText}</button>}

                            </ContentSpacer>
                            <div className={tableClasses.labelRowContainer}>
                                {layDc?.plan?.name && <LabelAndValue value={layDc?.plan?.name} label={settings.planNameLabel} />}
                                {layDc?.plan?.effective_date && <LabelAndValue value={getFormattedDate(layDc?.plan?.effective_date)} label={settings.adoptionDateLabel} />}
                                {layDc?.plan?.latest_amendment_date && <LabelAndValue value={getFormattedDate(layDc?.plan?.latest_amendment_date)} label={settings.lastAmendmentDateLabel} />}
                                {layDc?.plan?.employer_base && <LabelAndValue value={layDc?.plan?.employer_base} label={settings.employerBaseContributionLabel} />}
                                {layDc?.plan?.employer_match && <LabelAndValue value={layDc?.plan?.employer_match} label={settings.employerMatchContributionLabel} />}
                                {layDc?.plan?.minimum_scheduled_hours && <LabelAndValue value={layDc?.plan?.minimum_scheduled_hours} label={settings.minimumScheduledHourLabel} />}
                                {layDc?.plan?.allow_multiple_contribution_schedules && <LabelAndValue value={layDc?.plan?.allow_multiple_contribution_schedules} label={settings.allowMultipleContributionLabel} />}
                                {layDc?.plan?.vesting_schedule && <LabelAndValue value={layDc?.plan?.vesting_schedule} label={settings.vestingScheduleLabel} />}
                                {layDc?.plan?.status && <LabelAndValue value={layDc?.plan?.status} label={settings.planStatusLabel} />}
                            </div>
                        </div>
                    </AccordionDetails>
                </Accordion>
            }
            {
                rsvp &&
                <Accordion expanded={expand4} onChange={() => setExpand4(!expand4)}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <div className={headerClasses.iconHeader}>
                            <p>{rsvp.plan?.name}</p>
                            {RenderIconStatus(rsvp)}
                        </div>
                    </AccordionSummary>
                    <AccordionDetails >
                        <ContentSpacer>
                            {RenderStatus(rsvp)}
                            <Link to={`${planHistoryLink}?type=${PensionPlanTypes.Rsvp}`} >{settings?.planHistoryLinkText}</Link> | <Link to={`${qccoPlanAmendLink}?formType=${PensionPlanTypes.Rsvp}`} >{settings?.amendPlanLinkText}</Link>
                                {rsvp?.plan?.docusign_envelope_id && <button className={buttonClasses.linkButton} type='button' onClick={() => downloadPdfAction(rsvp?.plan?.docusign_envelope_id)} > | {settings?.downloadDocusignPdfLinkText}</button>}

                        </ContentSpacer>
                        <div className={tableClasses.labelRowContainer}>
                            {rsvp?.plan?.name && <LabelAndValue value={rsvp?.plan?.name} label={settings.planNameLabel} />}
                            {rsvp?.plan?.effective_date && <LabelAndValue value={getFormattedDate(rsvp?.plan?.effective_date)} label={settings.adoptionDateLabel} />}
                            {rsvp?.plan?.latest_amendment_date && <LabelAndValue value={getFormattedDate(rsvp?.plan?.latest_amendment_date)} label={settings.lastAmendmentDateLabel} />}
                            {rsvp?.plan?.employer_base && <LabelAndValue value={rsvp?.plan?.employer_base} label={settings.employerBaseContributionLabel} />}
                            {rsvp?.plan?.employer_match && <LabelAndValue value={rsvp?.plan?.employer_match} label={settings.employerMatchContributionLabel} />}
                            {rsvp?.plan?.minimum_scheduled_hours && <LabelAndValue value={rsvp?.plan?.minimum_scheduled_hours} label={settings.minimumScheduledHourLabel} />}
                            {rsvp?.plan?.allow_multiple_contribution_schedules && <LabelAndValue value={rsvp?.plan?.allow_multiple_contribution_schedules} label={settings.allowMultipleContributionLabel} />}
                            {rsvp?.plan?.eligible_population && <LabelAndValue value={rsvp?.plan?.eligible_population} label={settings.eligiblePopluationLabel} />}
                            {rsvp?.plan?.status && <LabelAndValue value={rsvp?.plan?.status} label={settings.planStatusLabel} />}
                        </div>
                    </AccordionDetails>
                </Accordion>
            }
        </>
    );
}

export default PensionPlanSummary;